/**
 * Pure derivation of each production step's status from StudioStore
 * state. Extracted from the old ChecklistPanel so the sidebar's
 * progress dots and any other surface share ONE source of truth for
 * "is this step empty / in progress / done".
 *
 * `empty`    — nothing started for this step yet.
 * `progress` — partially filled (e.g. some scenes cast, not all).
 * `done`     — complete enough to read as finished.
 */

import type { StudioPanelId } from "./PanelManager";
import type { StudioStore } from "./StudioStore";

export type StepStatus = "empty" | "progress" | "done";

/** Store keys whose changes can flip any step's status. */
export const STATUS_STORE_KEYS = [
  "scriptFilename",
  "scenes",
  "characters",
  "assets",
  "locations",
  "moodBoardItems",
  "costumeByCharacterId",
  "costumeLooksByCharacterId",
  "storyboardStatus",
  "videoStatus",
] as const;

export function computeStepStatus(
  store: StudioStore,
  stepId: StudioPanelId,
): StepStatus {
  const state = store.snapshot();
  switch (stepId) {
    case "script_upload":
      return state.scriptFilename ? "done" : "empty";
    case "script_editor":
      return state.scenes.length === 0
        ? "empty"
        : state.scenes.some((scene) => scene.body.trim().length === 0)
          ? "progress"
          : "done";
    case "timeline":
      return state.scenes.length === 0 ? "empty" : "done";
    case "characters":
      return state.characters.length === 0 ? "empty" : "done";
    case "assets":
      return state.assets.length === 0 ? "empty" : "done";
    case "moodboard":
      return state.moodBoardItems.length === 0 ? "empty" : "done";
    case "location":
      return state.locations.length === 0 ? "empty" : "done";
    case "storyboard":
      return state.storyboardStatus === "ready"
        ? "done"
        : state.scenes.some((scene) => scene.storyboardReady)
          ? "progress"
          : "empty";
    case "final_video":
      return state.videoStatus === "ready"
        ? "done"
        : state.videoStatus === "rendering"
          ? "progress"
          : "empty";
    default:
      return "empty";
  }
}
